import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import GoldRule from './GoldRule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standalone enquiry form embedded at the bottom of every set page —
// replaces the old dedicated /corporate page.
export const SetEnquiryForm = ({ setName }) => {
  const [form, setForm] = useState({ company: '', contact: '', email: '', quantity: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/corporate/enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, setInterest: setName }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Submission failed');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquire" className="hairline-t border-subtle" data-testid="set-enquiry-section">
      <div className="max-w-[900px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="eyebrow text-mb-muted mb-6">In quantity, or for a client</div>
        <h2 className="font-display text-3xl md:text-4xl text-mb">
          Enquire about <span className="italic">{setName}</span>.
        </h2>
        <div className="mt-8">
          <GoldRule size="med" />
        </div>

        {!submitted ? (
          <form onSubmit={onSubmit} className="mt-10 grid md:grid-cols-2 gap-6" data-testid="set-enquiry-form">
            <Field label="Company (optional)" testid="set-enquiry-company" value={form.company} onChange={update('company')} />
            <Field label="Contact name" testid="set-enquiry-contact" value={form.contact} onChange={update('contact')} required />
            <Field label="Email" testid="set-enquiry-email" type="email" value={form.email} onChange={update('email')} required />
            <Field label="Quantity" testid="set-enquiry-quantity" value={form.quantity} onChange={update('quantity')} placeholder="e.g. 1, or 12 for a programme" />
            <div className="md:col-span-2">
              <label className="mb-label">Message</label>
              <textarea
                data-testid="set-enquiry-message"
                className="mb-input"
                rows={4}
                value={form.message}
                onChange={update('message')}
                placeholder="Context, occasion, recipients…"
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-5">
              <button
                type="submit"
                className="btn-gold disabled:opacity-50"
                data-magnetic
                data-testid="set-enquiry-submit-btn"
                disabled={submitting}
              >
                {submitting ? 'Sending…' : 'Send enquiry'}
                <ArrowUpRight size={14} strokeWidth={1.25} />
              </button>
              <div className="eyebrow-sm text-mb-muted">We reply within one business day.</div>
            </div>
            {error && (
              <p className="md:col-span-2 text-[13px] text-red-400" data-testid="set-enquiry-error">
                {error}
              </p>
            )}
          </form>
        ) : (
          <div className="mt-10 hairline border-gold p-8 bg-mb-surface" data-testid="set-enquiry-confirmation">
            <div className="eyebrow text-gold mb-3">Noted.</div>
            <p className="text-[14px] text-mb-muted leading-relaxed">
              A single atelier representative will write to you within one business day.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, testid, value, onChange, type = 'text', placeholder, required }) => (
  <div>
    <label className="mb-label">{label}</label>
    <input
      data-testid={testid}
      type={type}
      className="mb-input"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
    />
  </div>
);

export default SetEnquiryForm;
